import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  IconButton,
} from "@mui/material";
import ArrowBackSharpIcon from "@mui/icons-material/ArrowBackSharp";
import { Helmet } from "react-helmet-async";
import { socketEventEmitter } from "../socket";
import { useProject } from "../context/ProjectContext";
import { useNotification } from "../context/NotificationContext";
import PageService from "../services/PageService";
import ProjectService from "../services/ProjectService";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import BreadcrumbsNav from "../components/BreadcrumbsNav";
import TestCaseActions from "../components/testCase/TestCaseActions";
import DetailsAccordion from "../components/testCase/DetailsAccordion";
import RegenerateTestCaseConfirmationModal from "../components/modals/RegenerateTestCaseConfirmationModal";
import CreateTestCaseModal from "pages/SingleJourney/components/TestCasesTab/components/CreateTestCaseModal";
import EditTestCasesConfirmationModal from "../components/modals/EditTestCaseConfirmationModal";
import DeleteTestCasesConfirmationModal from "../components/modals/DeleteTestCaseConfirmationModal";
import StepsAccordion from "../components/testCase/StepsAccordion";
import RunsAccordion from "../components/testCase/RunsAccordion";
import JourneyTestCaseService from "pages/SingleJourney/components/TestCasesTab/services";
import JourneyService from "pages/SingleJourney/services";
import { TEST_STATUS_STYLES } from "../theme/statusColors";

const TestCase = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const showNotification = useNotification();
  const { selectedProject, setSelectedProject } = useProject();

  const [testCase, setTestCase] = useState(null);
  const [testRuns, setTestRuns] = useState([]);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pageToggler, setPageToggler] = useState(false);
  const [status, setStatus] = useState("");
  const [uploadingSteps, setUploadingSteps] = useState({});
  const [journeyRegenerateLoading, setJourneyRegenerateLoading] =
    useState(false);
  const [journeyRegenerateContext, setJourneyRegenerateContext] = useState({
    requirements: "",
    expectedTestCases: "",
  });

  const getStatusLabel = (testCaseStatus) => {
    if (!testCaseStatus) return "";
    if (testCaseStatus === "RUNNING") return "In Progress...";
    if (testCaseStatus === "PENDING") return "Pending";
    if (testCaseStatus === "COMPLETED") return "Passed";
    if (testCaseStatus === "INITIAL_FAILURE") return "Initial failure";
    return testCaseStatus.toLowerCase();
  };

  const syncProjectSelection = useCallback(
    async (projectId) => {
      if (!projectId || selectedProject?.id === projectId) {
        return;
      }

      try {
        const projectData = await ProjectService.getProjectById(projectId);
        setSelectedProject(projectData);
      } catch (error) {
        console.error("Error syncing selected project:", error);
      }
    },
    [selectedProject?.id, setSelectedProject]
  );

  useEffect(() => {
    const getTestCase = async () => {
      try {
        const testCase = await PageService.getTestCaseById(id);
        setTestCase(testCase);
        setStatus(testCase.status);
        syncProjectSelection(testCase?.projectId);
      } catch (error) {
        console.error("Error fetching test case:", error);
      }
    };

    const getTestRuns = async () => {
      try {
        const testRunsData = await PageService.getAllTestRunsByTestCaseId(id);
        setTestRuns(testRunsData.runs);
      } catch (error) {
        console.error("Error fetching test runs:", error);
      }
    };

    getTestCase();
    getTestRuns();

    const handleTaskCompleted = async (data) => {
      if (data.testCaseId === id && data.type === "Test Case") {
        setPageToggler(!pageToggler);
      }
    };

    const handleTaskFailed = async (data) => {
      if (data.testCaseId === id && data.type === "Test Case") {
        setPageToggler(!pageToggler);
      }
    };

    socketEventEmitter.on("taskCompleted", handleTaskCompleted);
    socketEventEmitter.on("taskFailed", handleTaskFailed);

    return () => {
      socketEventEmitter.off("taskCompleted", handleTaskCompleted);
      socketEventEmitter.off("taskFailed", handleTaskFailed);
    };
    // eslint-disable-next-line
  }, [id, selectedProject?.id, pageToggler, syncProjectSelection]);

  const handleRunTestCase = async () => {
    try {
      const isDraft = String(testCase?.status || "").toUpperCase() === "DRAFT";
      setTestCase((prev) =>
        prev
          ? {
              ...prev,
              status: "PENDING",
            }
          : prev
      );
      setStatus("PENDING");
      showNotification(
        isDraft ? "Test case run has started" : "Test case re-running has started",
        "info",
        "Learn more",
        navigate
      );
      await PageService.runTestCase(testCase?.id);
      setPageToggler(!pageToggler);
    } catch (error) {
      console.log(error);
    }
  };

  const handleUpdateTestCaseStatus = async (event) => {
    const newStatus = event.target.value;
    setStatus(newStatus);

    try {
      await PageService.updateTestCaseById(id, {
        last_run_status: newStatus,
      });
      setPageToggler(!pageToggler);
    } catch (error) {
      console.error("Error updating test case status:", error);
    }
  };

  const handleUpdateStepValue = async (id, newValue) => {
    try {
      await PageService.updateTestCaseStepValue(id, newValue);
      setPageToggler(!pageToggler);
    } catch (error) {
      console.error("Error updating test case status:", error);
    }
  };

  const handleUploadStepFile = async (stepId, file) => {
    setUploadingSteps((prev) => ({ ...prev, [stepId]: true }));
    try {
      await PageService.uploadTestCaseStepFile(stepId, file);
      showNotification("File uploaded for the test step", "success");
      setPageToggler((prev) => !prev);
    } catch (error) {
      console.error("Error uploading file for test case step:", error);
      showNotification("Failed to upload file for the test step", "error");
    } finally {
      setUploadingSteps((prev) => {
        const next = { ...prev };
        delete next[stepId];
        return next;
      });
    }
  };

  const handleGenerateNegative = async () => {
    try {
      showNotification(
        "Discovering negative scenarios has started",
        "info",
        "Learn more",
        navigate
      );
      await PageService.generateNegativeScenarios(testCase?.id);
      setPageToggler(!pageToggler);
    } catch (error) {
      console.log(error);
    }
  };

  const handleJourneyRegenerate = async (contextData = null) => {
    if (!testCase?.id || !testCase?.journey?.id) return;
    try {
      setJourneyRegenerateLoading(true);
      showNotification("Journey test case generation has started", "info", "Learn more", navigate);
      await JourneyTestCaseService.regenerateTestCases({
        journeyId: testCase.journey.id,
        idsToRegenerate: [testCase.id],
        regenerateAll: false,
        contextData,
      });
    } catch (error) {
      showNotification(
        `Error! ${error?.response?.data?.message || "Something went wrong"}`,
        "error"
      );
    } finally {
      setJourneyRegenerateLoading(false);
      setRegenerateOpen(false);
    }
  };

  const handleOpenRegenerateDialog = () => {
    setRegenerateOpen(true);
    if (testCase?.journey?.id) {
      setJourneyRegenerateContext({
        requirements: "",
        expectedTestCases: "",
      });
      (async () => {
        try {
          const journey = await JourneyService.getJourneyById(
            testCase.journey.id
          );
          setJourneyRegenerateContext({
            requirements: journey?.user_requirements || "",
            expectedTestCases: journey?.user_expected_test_cases || "",
          });
        } catch (error) {
          console.error("Failed to load journey context:", error);
        }
      })();
    }
  };

  const handleOpenEditDialog = () => {
    setEditOpen(true);
  };

  const handleOpenDeleteDialog = () => {
    setDeleteOpen(true);
  };

  const handleBackClick = () => {
    if (testCase?.journey?.id) {
      navigate(`/test-journey/${testCase?.journey?.id}`, {
        state: { activeTab: "testCases" },
      });
      return;
    }
    navigate(`/pages/${testCase.pageId}`, {
      state: { activeTab: "3" },
    });
  };

  return (
    <Box className="testCase rootWrapper">
      <Helmet>
        <title>{`Treegress - ${selectedProject.name} ${testCase?.pageLabel} ${testCase?.testCaseName}`}</title>
      </Helmet>
      <Header />
      <Box className="srollContent">
        <Sidebar />
        <Box className="testCaseContentContainer">
          <BreadcrumbsNav
            pageLabel={testCase?.journey?.id ? "journeyTestCase" : "testCase"}
            pageName={testCase?.pageLabel}
            pageId={testCase?.pageId}
            testCaseName={testCase?.testCaseName}
            journey={testCase?.journey}
          />
          <Box className="testCaseTopSection">
            <Box className="testCaseTitleContainer">
              <IconButton
                className="testCaseBackButton"
                size="small"
                onClick={handleBackClick}
                sx={{
                  width: 32,
                  height: 32,
                  border: "1px solid #2D4356",
                  backgroundColor: "#fff",
                  color: "#2D4356",
                  "&:hover": {
                    backgroundColor: "#f5f7fb",
                  },
                }}
              >
                <ArrowBackSharpIcon fontSize="small" />
              </IconButton>
              <Typography
                className="testCaseTitle"
                variant="h6"
                color="primary"
              >
                {testCase?.testCaseName}
              </Typography>
              {testCase?.status === "FAILED" ||
                testCase?.status === "COMPLETED" ? (
                <FormControl>
                  <Select
                    className="testCaseStatus"
                    value={status}
                    onChange={handleUpdateTestCaseStatus}
                    sx={{
                      ...TEST_STATUS_STYLES[status],
                      "& .MuiSelect-icon": {
                        color: "#fff",
                        marginRight: "-4px",
                      },
                      padding: "0 !important",
                    }}
                  >
                    <MenuItem value="FAILED">Failed</MenuItem>
                    <MenuItem value="COMPLETED">Passed</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Tooltip
                    title={
                      testCase?.status === "INITIAL_FAILURE"
                        ? 'The test case failed on the first run, which indicates a potential issue in the positive flow. Negative scenarios were not generated automatically. You can review the test result, fix the issue and regenerate tests, or manually generate negative scenarios using the "Generate negative scenarios" button.'
                        : ""
                    }
                    disableHoverListener={
                      testCase?.status !== "INITIAL_FAILURE"
                    }
                    PopperProps={{
                      sx: {
                        "& .MuiTooltip-tooltip": {
                          backgroundColor: "primary.main",
                        },
                      },
                    }}
                  >
                    <Box
                      className="testCaseStatus"
                      sx={{
                        ...TEST_STATUS_STYLES[testCase?.status],
                      }}
                    >
                      {getStatusLabel(testCase?.status)}
                    </Box>
                  </Tooltip>
                </Box>
              )}
            </Box>
            <TestCaseActions
              testCase={testCase}
              handleOpenRegenerateDialog={handleOpenRegenerateDialog}
              handleOpenEditDialog={handleOpenEditDialog}
              handleOpenDeleteDialog={handleOpenDeleteDialog}
              handleRunTestCase={handleRunTestCase}
              handleGenerateNegative={handleGenerateNegative}
            />
          </Box>
          <Box className="testCaseMainSection">
            {testCase && (testRuns.length || testCase.status !== "RUNNING") ? (
              <>
                <DetailsAccordion testCase={testCase} testRuns={testRuns} />
                <StepsAccordion
                  testCase={testCase}
                  handleUpdateStepValue={handleUpdateStepValue}
                  handleUploadStepFile={handleUploadStepFile}
                  uploadingSteps={uploadingSteps}
                />
                <RunsAccordion testCase={testCase} testRuns={testRuns} />
              </>
            ) : (
              <Box sx={{ mt: 2, width: "100%" }}>
                <Typography mb={1}>Test case running...</Typography>
                <LinearProgress color="secondary" />
              </Box>
            )}
          </Box>
          {testCase?.journey?.id ? (
            <CreateTestCaseModal
              open={regenerateOpen}
              onClose={() => setRegenerateOpen(false)}
              userRequirements={journeyRegenerateContext.requirements}
              userExpectedTestCases={
                journeyRegenerateContext.expectedTestCases
              }
              mode="regenerate"
              onGenerate={handleJourneyRegenerate}
              isGeneration={journeyRegenerateLoading}
            />
          ) : (
            <RegenerateTestCaseConfirmationModal
              testCase={testCase}
              regenerateOpen={regenerateOpen}
              setRegenerateOpen={setRegenerateOpen}
            />
          )}
          <EditTestCasesConfirmationModal
            testCase={testCase}
            editOpen={editOpen}
            setEditOpen={setEditOpen}
            pageToggler={pageToggler}
            setPageToggler={setPageToggler}
          />
          <DeleteTestCasesConfirmationModal
            testCase={testCase}
            deleteOpen={deleteOpen}
            setDeleteOpen={setDeleteOpen}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default TestCase;
